"use client";

import type React from "react";
import Link from "next/link";
import { useMemo, useState } from "react";
import {
  Factory,
  Wrench,
  Cog,
  Hammer,
  Sparkles,
  Boxes,
  Layers,
  ArrowUpRight,
} from "lucide-react";
import { useInView } from "@/hooks/use-in-view";
import SectionHeading from "./section-heading";

type Item = {
  key: string;
  title: string;
  bullets: string[];
  icon: React.ElementType;
  href: string;
};

const items: Item[] = [
  {
    key: "casting",
    title: "Casting",
    bullets: [
      "Die casting",
      "Sand casting",
      "Investment casting",
      "Perfume mold casting",
    ],
    icon: Layers,
    href: "/casting",
  },
  {
    key: "molding",
    title: "Molding",
    bullets: ["Thermoforming", "Injection Molding", "Rotational Molding"],
    icon: Layers,
    href: "/molding",
  },
  {
    key: "tube",
    title: "Tube Bending",
    bullets: [
      "End Tubing",
      "5 Axis CNC",
      "Bead Forming",
      "Precision Cutting",
      "Brazed Assemblies",
      "Post-Process Cleaning",
    ],
    icon: Wrench,
    href: "/tube-bending",
  },
  {
    key: "machining",
    title: "Machining",
    bullets: ["Turning", "Milling", "Drilling", "Boring", "Honing", "Grinding"],
    icon: Cog,
    href: "/cnc",
  },
  {
    key: "stamping",
    title: "Stamping",
    bullets: [
      "Laser",
      "Drawing",
      "Short Run",
      "Fine Blanking",
      "Progressive Die",
    ],
    icon: Hammer,
    href: "/stamping-services",
  },
  {
    key: "forging",
    title: "Forging",
    bullets: ["Open die", "Closed Die", "Up to 1500Kg"],
    icon: Factory,
    href: "/forging",
  },
  {
    key: "finishing",
    title: "Finishing",
    bullets: [
      "Pickling",
      "Plating",
      "Anodizing",
      "Degreasing",
      "Passivation",
      "Wet Painting",
      "Powder Coating",
      "Vapor Degreasing",
    ],
    icon: Sparkles,
    href: "/finishing",
  },
  {
    key: "assembly",
    title: "Assembly and Fabrication",
    bullets: [
      "Welding",
      "Riveting",
      "Brazing",
      "Fastening",
      "Adhesive Bonding",
    ],
    icon: Boxes,
    href: "/assembly-fabrication",
  },
];

export default function Capabilities() {
  const [active, setActive] = useState("casting");
  const activeItem = useMemo(
    () => items.find((i) => i.key === active) || items[0],
    [active]
  );
  const { ref, inView } = useInView<HTMLDivElement>({
    rootMargin: "-80px 0px",
  });

  const imageUrls: Record<string, string> = {
    casting: "/homepage/3.jpeg",
    molding: "/homepage/molding.jpeg",
    tube: "/homepage/tube.jpg",
    machining: "/homepage/maching.jpeg",
    stamping: "/homepage/stamping.jpeg",
    forging: "/homepage/forging.jpg",
    finishing: "/homepage/finishingg.jpg",
    assembly: "/homepage/assembly.jpeg",
  };

  return (
    <div
      ref={ref}
      className={`${
        inView
          ? "opacity-100 translate-y-0 transition-all duration-700"
          : "opacity-0 translate-y-3"
      }`}
    >
      {/* Heading sized per your mobile standard */}
      <SectionHeading
        subtitle={
          <span className="text-[16px] sm:text-[21px] md:text-[22px] font-normal text-[#666666]">
            Our streamlined process ensures your shipments are handled
            efficiently.
          </span>
        }
        headingColor="text-[#111111]"
        underlineColor="bg-[#FDC300]"
        className="mt-6 md:mt-14" // ✅ reduced gap — balanced across screen sizes
      >
        <span className="text-[23px] sm:text-[24px] md:text-[40px]">
          Our Capabilities
        </span>
      </SectionHeading>

      {/* MOBILE TABS (Services-style) — horizontal scroll, no fog */}
      <div className="mt-6 lg:hidden">
        <nav
          role="tablist"
          aria-label="Capabilities"
          className="relative w-full border-b border-[var(--border)]"
        >
          <div className="relative -mx-4 px-4">
            <div
              className="
                flex gap-2 sm:gap-3 overflow-x-auto whitespace-nowrap
                snap-x snap-mandatory scroll-px-4
                [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
              "
            >
              {items.map((t) => {
                const isActive = t.key === active;
                return (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`cap-panel-${t.key}`}
                    onClick={() => setActive(t.key)}
                    className={[
                      "shrink-0 snap-start relative flex items-center justify-center transition-all duration-300",
                      "h-12 sm:h-14 px-3 sm:px-4 rounded-t-2xl",

                      // ✅ All tabs black (mobile)
                      "text-black",

                      isActive
                        ? `
      tab-active-gradient
      text-[16px] sm:text-[18px] font-bold
      scale-105
      after:absolute after:left-0 after:right-0 after:-bottom-[1px]
      after:h-[3px] after:rounded-t-full after:bg-[#FDC300]
    `
                        : "text-[14px] sm:text-[15px] font-medium opacity-80",
                    ].join(" ")}
                  >
                    {t.title}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      </div>

      <div className="mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-12 items-start gap-6 lg:gap-0">
        {/* DESKTOP/LG: original icon list — hidden on mobile */}
        <aside className="hidden lg:block lg:col-span-3 relative min-w-0">
          <ul
            role="tablist"
            aria-label="Capabilities"
            className="relative block gap-4 lg:overflow-visible lg:pl-[20px]"
          >
            {items.map((i, idx) => {
              const Icon = i.icon;
              const isActive = i.key === active;
              return (
                <li
                  key={i.key}
                  className={`relative ${
                    idx !== items.length - 1 ? "mb-6" : ""
                  }`}
                >
                  <button
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`cap-panel-${i.key}`}
                    onClick={() => setActive(i.key)}
                    className="group flex items-center gap-4 transition-all duration-300 hover:opacity-80"
                  >
                    <span
                      className={[
                        "relative z-10 grid place-items-center rounded-full transition-all duration-300",
                        "w-[55px] h-[55px]",
                        isActive
                          ? "bg-[#FDC300] text-black shadow-[0_0_0_3px_white,0_0_0_4px_#FDC300]"
                          : "bg-[#FFF8E1] text-black/70 hover:bg-[#FFF3CD]",
                      ].join(" ")}
                      aria-hidden
                    >
                      <Icon className="h-6 w-6" strokeWidth={2} />
                    </span>
                    <span
                      className={`text-left transition-all duration-300 ${
                        isActive
                          ? "text-[18px] lg:text-[24px] font-bold text-black"
                          : "text-[15px] lg:text-[16px] font-medium text-black opacity-80"
                      }`}
                    >
                      {i.title}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="absolute top-0 right-[-20px] h-full w-[2px] bg-[#FDC300]" />
        </aside>

        {/* RIGHT BLOCK (content + image) */}
        <div className="lg:col-span-9">
          <div className="grid lg:grid-cols-12 items-center gap-6 lg:gap-12">
            {/* Text content */}
            <div className="lg:col-span-7 min-w-0 lg:pl-18">
              <div
                key={active}
                id={`cap-panel-${activeItem.key}`}
                role="tabpanel"
                aria-labelledby={active}
                className="animate-cap-fade"
              >
                {/* Mobile/tablet title sizes */}
                <h3 className="text-center text-[28px] sm:text-[32px] md:text-[36px] lg:text-[40px] font-bold text-[#222] leading-[1.2]">
                  {activeItem.title}
                </h3>
                <div className="w-14 h-[4px] bg-[#FDC300] mt-3 mb-6 mx-auto rounded-full" />
                {/* Bullets: 2 columns on mobile, back to 1 on lg */}
                <ul className="mt-4 sm:mt-5 max-w-[420px] mx-auto grid grid-cols-2 gap-x-6 gap-y-2 lg:grid-cols-1 lg:space-y-2.5 text-center">
                  {" "}
                  {activeItem.bullets.map((b, idx) => (
                    <li
                      key={b}
                      className="flex items-center justify-center opacity-0 animate-cap-stagger"
                      style={{ animationDelay: `${120 + idx * 60}ms` }}
                    >
                      <span className="text-[#777777] mr-2">•</span>
                      <span className="text-[14px] sm:text-[15px] md:text-[16px] leading-[1.6] text-[#555555]">
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Desktop button under text */}
                <Link
                  href={activeItem.href}
                  className="hidden lg:flex mt-7 items-center justify-center mx-auto w-fit rounded-[30px] bg-[#FDC300] px-[22px] py-[10px] font-semibold text-black hover:brightness-95 transition-all"
                >
                  Learn More
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Image + Mobile overlay button */}
            <div className="lg:col-span-5 min-w-0 lg:-ml-10 relative">
              <img
                key={active}
                src={imageUrls[active] || imageUrls.casting}
                alt={`${activeItem.title} process`}
                className="w-full max-w-[660px] aspect-[4/3] rounded-2xl object-cover object-center shadow-md opacity-0 animate-cap-image"
              />

              {/* Mobile button inside image */}
              <Link
                href={activeItem.href}
                className="lg:hidden absolute bottom-4 left-4 inline-flex items-center rounded-[30px] bg-[#FDC300] px-[18px] py-[9px] text-[14px] font-semibold text-black shadow-sm hover:brightness-95"
              >
                Learn More
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
